import { MapPinIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { CLASE_DE_TARJETA_RESALTADA } from "./MarcasDeSincronizacion";
import { useResaltadoDeLaCaptura } from "./PanelDeTerrenoDeLaDescripcion";
import { momentoDeSincronizacion } from "./registroDeTerreno";
import type { PuntoDeAcceso } from "./terrenoDeAntecedentes";

type PuntoDeAccesoDeTerrenoProps =
  /** El encargado confirmó un punto y el formulario todavía no lo usa. */
  | { estado: "disponible"; punto: PuntoDeAcceso; distancia: string | null; onUsar: () => void }
  /** El formulario YA tiene ese punto. `onVolver` ausente = no hay a qué volver. */
  | { estado: "en_uso"; punto: PuntoDeAcceso; onVolver?: () => void }
  /** La visita todavía no sincronizó: la ubicación sigue siendo la de la cotización. */
  | { estado: "pendiente" };

function momentos(punto: PuntoDeAcceso): string {
  return `Capturado ${momentoDeSincronizacion(punto.capturado_en)} · sincronizado ${momentoDeSincronizacion(punto.sincronizado_en)}`;
}

/**
 * Lo que la visita a terreno sabe de la ubicación del predio, sobre el mapa de
 * la tarjeta "Ubicación del predio": un aviso con tres caras.
 *
 * NO escribe nada: "Usar ubicación de terreno" le avisa al formulario, que
 * cambia su coordenada y la deja sucia como cualquier otro cambio. Guardarla es
 * un acto aparte ("Guardar cambios"), igual que si la hubiera tecleado.
 */
export function PuntoDeAccesoDeTerreno(props: PuntoDeAccesoDeTerrenoProps) {
  const marca = useResaltadoDeLaCaptura(props.estado === "pendiente" ? null : props.punto.sincronizacion_id);

  if (props.estado === "pendiente") {
    return (
      <div className="border-warning-dot/50 bg-warning-soft text-warning-soft-foreground flex items-center gap-3 rounded-xl border-[1.5px] border-dashed px-3.5 py-2.5">
        <MapPinIcon className="size-[1.0625rem] flex-none" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-xs font-semibold text-pretty">
          Ubicación referencial, tomada de la cotización. La visita en terreno la confirmará con el
          GPS del punto de acceso.
        </p>
      </div>
    );
  }

  const { punto } = props;

  if (props.estado === "en_uso") {
    return (
      <div className="border-border bg-superficie-sutil flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-2.5">
        <div className="text-ink-body min-w-0 flex-1 text-xs font-semibold">
          Se usa el punto de acceso capturado en la visita
          {punto.precision_m !== null ? ` · ± ${punto.precision_m} m` : ""} ·{" "}
          <span className="font-mono">{momentos(punto)}</span>
        </div>
        {props.onVolver ? (
          <Button type="button" variant="ghost" size="sm" onClick={props.onVolver}>
            Volver a la anterior
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      {...marca.atributo}
      className={cn(
        "flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-3",
        marca.on ? CLASE_DE_TARJETA_RESALTADA : "border-success-dot/40 bg-success-soft/40",
      )}
    >
      <MapPinIcon className="text-success-soft-foreground size-[1.0625rem] flex-none" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-ink flex items-center gap-2 text-[0.78125rem] font-bold">
          Punto de acceso confirmado en terreno
          {marca.tag}
        </div>
        <div className="text-ink-body mt-0.5 text-xs font-semibold">
          <span className="text-ink font-mono font-bold">
            {punto.latitud}, {punto.longitud}
          </span>
          {punto.precision_m !== null ? ` · ± ${punto.precision_m} m` : ""}
          {props.distancia ? ` · ${props.distancia} del punto marcado` : ""}
        </div>
        <div className="text-ink-muted mt-0.5 font-mono text-[0.65625rem] font-semibold">
          {momentos(punto)}
        </div>
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={props.onUsar}>
        Usar ubicación de terreno
      </Button>
    </div>
  );
}
